import React, { createContext, useContext, useMemo, useState } from 'react';
import { Slide } from 'pure-react-carousel';
import { useHistory } from 'react-router-dom';
import css from './SectionRewards.css';
import classNames from 'classnames';
import { Button } from '../../../../components';
import { AwardCard as Card } from './AwardCard';
import { createResourceLocatorString } from '../../../../util/routes';
import routeConfiguration from '../../../../routeConfiguration';
import { ConditionalWrapper } from '../../../../util/common';
import { useIsMobile } from '../../../../hooks/useIsMobile';
import Carousel from '../../../../components/ListingCardsCarousel/Carousel';
import {
    allAwardTypes,
    AWARD_TYPE_ALL,
    AWARD_TYPE_HORSE_PROFILE,
    AWARD_TYPE_RIDER_PROFILE,
} from './awardType';
import { useAwardsCards } from './useAwardsCards';
import { useSelector } from 'react-redux';
import { sortAwardsByActiveAction } from './utils';
import config from '../../../../config';

const { userTypeHorseowner, userTypeRider } = config;

const RewardsContext = createContext();

const SectionRewards = ({ children, showSlider: showSliderProp = true }) => {
    const history = useHistory();
    const [isMobile, _, computing, viewport] = useIsMobile(1024);
    const isTablet = viewport > 768 && viewport < 1024;

    const currentUser = useSelector(s => s.user.currentUser);
    const { userType } = currentUser?.attributes?.profile?.publicData || {};

    const [awardSelected, setAwardSelected] = useState(AWARD_TYPE_ALL);

    const navigate = (name, pathParams = {}, searchParams = {}) =>
        history.push(
            createResourceLocatorString(name, routeConfiguration(), pathParams, searchParams)
        );

    const awardCards = useAwardsCards(navigate);

    const availableTabs = useMemo(() => {
        if (userType === userTypeRider)
            return allAwardTypes.filter(t => t !== AWARD_TYPE_HORSE_PROFILE);

        if (userType === userTypeHorseowner)
            return allAwardTypes.filter(t => t !== AWARD_TYPE_RIDER_PROFILE);

        return allAwardTypes;
    }, [userType]);

    const filteredAwards = useMemo(() => {
        if (!awardCards) return [];
        const allTypesSelected = awardSelected === AWARD_TYPE_ALL;
        const filtered = allTypesSelected
            ? awardCards
            : awardCards.filter(a => a.type === awardSelected);

        return allTypesSelected ? sortAwardsByActiveAction(filtered, isMobile) : filtered;
    }, [awardCards, awardSelected, isMobile]);

    const minNumToShowSlider = isTablet ? 2.1 : isMobile ? 1 : 3;
    const showSlider = showSliderProp && filteredAwards.length > minNumToShowSlider;

    const value = {
        awardSelected,
        setAwardSelected,
        availableTabs,
        filteredAwards,
        showSlider,
        minNumToShowSlider,
        isMobile,
        isTablet,
        loading: computing,
    };

    return (
        <RewardsContext.Provider value={value}>
            <section className={css.root} id="rewards">
                {children}
            </section>
        </RewardsContext.Provider>
    );
};

const Header = ({ title, description, children }) =>
    children || (
        <header>
            {title && <h2 className={css.heading}>{title}</h2>}
            {description && <p className={css.description}>{description}</p>}
        </header>
    );

const Tabs = () => {
    const { availableTabs, awardSelected, setAwardSelected } = useContext(RewardsContext);
    return (
        <nav className={css.tabsNavigation}>
            {availableTabs.map(tab => (
                <Button
                    key={tab}
                    onClick={() => setAwardSelected(tab)}
                    className={classNames(css.tab, { [css.tabSelected]: tab === awardSelected })}
                >
                    {tab}
                </Button>
            ))}
        </nav>
    );
};

const Content = ({ rootClassName, cardClassName }) => {
    const {
        filteredAwards,
        loading,
        showSlider,
        minNumToShowSlider,
        isMobile,
        isTablet,
        awardSelected,
    } = useContext(RewardsContext);

    if (loading || !filteredAwards.length) return null;

    return (
        <div
            className={classNames({
                [css.cardsHolder]: !showSlider,
                [rootClassName]: !!rootClassName,
            })}
        >
            <ConditionalWrapper
                condition={showSlider}
                wrapper={children => (
                    <Carousel
                        key={`${awardSelected}-${isMobile}-${isTablet}`}
                        isMobile={isMobile}
                        items={children}
                        sliderClassName={css.slider}
                        sliderVisibleSlides={minNumToShowSlider}
                        hideButtonsOnEndStart
                        rendererCarousel={(card, index) => (
                            <Slide index={index} key={index}>
                                {card}
                            </Slide>
                        )}
                        rendererSingleItem={card => card}
                    />
                )}
            >
                {filteredAwards.map(
                    ({
                        id,
                        credits,
                        heading,
                        description,
                        actionText,
                        actionSubLabel,
                        actionHandler,
                        actionAllowed,
                        renderButton,
                    }) => (
                        <Card key={id} rootClassName={cardClassName}>
                            <Card.Badge>{credits}</Card.Badge>
                            <Card.Header>{heading}</Card.Header>
                            <Card.Description>{description}</Card.Description>
                            <Card.Action
                                disabled={!actionAllowed}
                                onClick={actionHandler}
                                actionText={actionText}
                                actionSubLabel={actionSubLabel}
                                renderButton={renderButton}
                            />
                        </Card>
                    )
                )}
            </ConditionalWrapper>
        </div>
    );
};
SectionRewards.Header = Header;
SectionRewards.Tabs = Tabs;
SectionRewards.Content = Content;

export default SectionRewards;
